
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Heart, X, Eye, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFavorites } from '@/hooks/useFavorites';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

const defaultProductImage = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";

interface Product {
  id: string;
  name: string;
  image: string;
  price: number;
  points_price: number;
}

const FavoritesDrawer = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { favorites, toggleFavorite } = useFavorites();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  // Buscar informações completas dos produtos favoritos
  const { data: favoriteProducts, isLoading, refetch } = useQuery({
    queryKey: ['favorite-products', favorites],
    queryFn: async () => {
      if (!user || favorites.length === 0) return [];
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .in('id', favorites);
        
      if (error) throw error;
      return data as Product[];
    },
    enabled: !!user && favorites.length > 0,
  });

  // Force refetch when favorites change
  React.useEffect(() => {
    if (user) {
      refetch();
    }
  }, [favorites.length, user, refetch]);

  const handleRemoveFavorite = (productId: string) => {
    toggleFavorite(productId);
    queryClient.invalidateQueries({ queryKey: ['favorite-products'] });
    toast({
      description: t('favoritesDrawer.removedFromFavorites'),
      className: "bg-white text-black border-gray-300",
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          onClick={() => {
            if (!user) {
              toast({
                title: t('favoritesDrawer.loginRequired'),
                description: t('favoritesDrawer.loginRequiredDesc'),
                variant: "destructive",
              });
              return;
            }
            setOpen(true);
          }}
        >
          <Heart className="h-5 w-5 text-gray-200" />
          {user && favorites.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-brand-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {favorites.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[350px] sm:w-[450px] bg-white p-0">
        <SheetHeader className="p-6 border-b">
          <SheetTitle className="flex items-center">
            <Heart className="h-5 w-5 mr-2 text-brand-red" /> 
            {t('header.favorites')}
          </SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] p-6">
          {isLoading ? (
            <div className="flex flex-col space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex">
                  <div className="w-16 h-16 bg-gray-200 rounded"></div>
                  <div className="ml-4 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : favoriteProducts && favoriteProducts.length > 0 ? (
            <div className="flex flex-col space-y-4">
              {favoriteProducts.map((product) => (
                <div key={product.id} className="flex border-b pb-4 group">
                  <img 
                    src={product.image || defaultProductImage}
                    alt={product.name}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultProductImage;
                    }}
                  />
                  <div className="ml-4 flex-1">
                    <h3 className="font-medium text-gray-800 truncate">{product.name}</h3>
                    <div className="flex justify-between mt-1">
                      <p className="text-brand-red font-medium">{product.points_price} {t('products.points')}</p>
                      {product.price && product.price > 0 && (
                        <p className="text-xs text-gray-500 line-through">€{product.price.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="flex space-x-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 px-2 text-gray-600 hover:text-brand-red"
                        asChild
                      >
                        <Link to={`/product/${product.id}`}>
                          <Eye className="h-4 w-4 mr-1" /> {t('favoritesDrawer.viewProduct')}
                        </Link>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 px-2 text-gray-600 hover:text-red-600"
                        onClick={() => handleRemoveFavorite(product.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> {t('products.remove')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Heart className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t('favoritesDrawer.noFavoritesYet')}</p>
              <Button 
                className="mt-4 bg-brand-red hover:bg-brand-red/90"
                asChild
              >
                <Link to="/shop">{t('favoritesDrawer.exploreProducts')}</Link>
              </Button>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default FavoritesDrawer;
